<script setup lang="ts">
import * as Sentry from '@sentry/vue';

import { useAppStore } from '@/store/app';
import { useUserStore } from '@/store/user';
import { useCartStore } from '@/store/cart';
import { logout } from '@/utils/helper/auth';
import { getUriWithParam, uid } from '@/utils/helper';
import { openMessageSessionInvalid } from '@/utils/helper/auth';
import { getAccountLink } from '@/utils/api/auth';
import { TOKEN_ERROR_CODE } from '@/constants';

const emit = defineEmits(['menu-click']);

const userStore = useUserStore();
const appStore = useAppStore();
const cartStore = useCartStore();

const router = useRouter();

const routePath = computed(() => router.currentRoute.value.path);

const userEmail = computed(() => {
	return userStore.getUserInfo ? userStore.getUserInfo.email : '';
});
const userLanguage = computed(() => appStore.getLanguage);
const accountUrl = computed(() => appStore.getMyAccountUrl);

const handleLogout = async () => {
	logout();

	cartStore.resetCart();
};

const routeToTransactionHistory = () => {
	emit('menu-click');
	router.push({ name: 'transaction-history' });
};

const routeToMyCards = () => {
	emit('menu-click');
	router.push({ name: 'my-cards' });
};

const getMyAccountUrl = computed(() => {
	if (!accountUrl.value) return '';
	const lang = useLowerCase(userLanguage.value.value);
	const url = getUriWithParam(accountUrl.value, { lang });
	return url;
});

const openUserProfile = async () => {
	try {
		const res = await getAccountLink(userLanguage.value.value);
		if (res?.centerLink) {
			appStore.updateMyAccountUrl(res.centerLink);
		}
		window.open(getMyAccountUrl.value, '_blank');
	} catch (error: any) {
		if (error.response.data.errorCode === TOKEN_ERROR_CODE.INVALID_TOKEN_AUTH) {
			openMessageSessionInvalid();
		} else {
			Sentry.captureException(error);
		}
	}
};
</script>
<template>
	<div class="user-menu">
		<a id="my-profile-btn" class="user-menu-section no-underline text-normal" @click="openUserProfile">
			<div class="flex items-center">
				<Avatar :size="60"></Avatar>
				<div class="user-name ml-2">
					<!--<div class="name text-xl font-semibold">John Doe</div>-->
					<div class="email font-semibold">{{ userEmail }}</div>
				</div>
			</div>
			<nuxt-icon class="ml-2" name="arrow-right" filled></nuxt-icon>
		</a>
		<!--<div id="membership-card-btn" class="user-menu-section">
			<div class="flex items-center">
				<nuxt-icon name="star" class="text-2xl" filled></nuxt-icon>
				<span class="ml-2"> {{ $t('membershipCard') }} </span>
			</div>
			<nuxt-icon class="ml-2" name="arrow-right" filled></nuxt-icon>
		</div>-->
		<div
			id="purchase-history-btn"
			class="user-menu-section"
			:class="{ 'is-active': routePath.includes('transaction-history') }"
			@click="routeToTransactionHistory"
		>
			<div class="flex">
				<nuxt-icon name="cart" class="text-2xl" filled></nuxt-icon>
				<span class="ml-2"> {{ $t('purchaseHistory') }} </span>
			</div>
			<nuxt-icon class="ml-2" name="arrow-right" filled></nuxt-icon>
		</div>
		<div
			id="my-cards-btn"
			class="user-menu-section"
			:class="{ 'is-active': routePath.includes('my-cards') }"
			@click="routeToMyCards"
		>
			<div class="flex">
				<nuxt-icon name="ticket" class="text-2xl custom-icon" filled></nuxt-icon>
				<span class="ml-2"> {{ $t('myCards') }} </span>
			</div>
			<nuxt-icon class="ml-2" name="arrow-right" filled></nuxt-icon>
		</div>
		<div id="support-center-btn" class="user-menu-section">
			<a
				id="support-center-link"
				href="https://support.vnggames.com/products/vnggames-card-722-773ace1f579d3732c938e6b32f6a808b/problems"
				class="flex w-full text-normal"
			>
				<nuxt-icon name="chat" class="text-2xl" filled></nuxt-icon>
				<span class="ml-2"> {{ $t('supportCenter') }} </span>
			</a>
			<nuxt-icon class="ml-2" name="arrow-right" filled></nuxt-icon>
		</div>
		<div id="logout-btn" class="user-menu-section" @click="handleLogout">
			<div class="flex items-center">
				<nuxt-icon name="logout" class="text-2xl" filled></nuxt-icon>
				<span class="ml-2"> {{ $t('logout') }} </span>
			</div>
		</div>
	</div>
</template>
<style lang="scss" scoped>
@import '@/assets/scss/components/user-menu.scss';
</style>
